// Lifely Mock Data Store - In-Memory State

// Contact Categories (alphabetically sorted, other last)
export const CONTACT_CATEGORIES = [
  { value: 'family', label: 'Family' },
  { value: 'financial', label: 'Financial' },
  { value: 'legal', label: 'Legal' },
  { value: 'medical', label: 'Medical' },
  { value: 'professional', label: 'Professional' },
  { value: 'trusted', label: 'Trusted Person' },
  { value: 'other', label: 'Other' },
];

// Contact Roles/Relationships (alphabetically sorted within each category, other last)
export const CONTACT_ROLES = [
  // Family (alphabetical)
  { value: 'child', label: 'Child', category: 'family' },
  { value: 'parent', label: 'Parent', category: 'family' },
  { value: 'sibling', label: 'Sibling', category: 'family' },
  { value: 'spouse', label: 'Spouse / Partner', category: 'family' },
  { value: 'relative', label: 'Other Relative', category: 'family' },
  // Financial (alphabetical)
  { value: 'accountant', label: 'Accountant', category: 'financial' },
  { value: 'banker', label: 'Banker', category: 'financial' },
  { value: 'financial-advisor', label: 'Financial Advisor', category: 'financial' },
  { value: 'insurance-agent', label: 'Insurance Agent', category: 'financial' },
  // Legal (alphabetical)
  { value: 'executor', label: 'Executor', category: 'legal' },
  { value: 'lawyer', label: 'Lawyer / Solicitor', category: 'legal' },
  { value: 'trustee', label: 'Trustee', category: 'legal' },
  // Medical (alphabetical)
  { value: 'doctor', label: 'Doctor', category: 'medical' },
  { value: 'specialist', label: 'Medical Specialist', category: 'medical' },
  // Professional (alphabetical)
  { value: 'business-partner', label: 'Business Partner', category: 'professional' },
  { value: 'colleague', label: 'Colleague', category: 'professional' },
  // Trusted Person (alphabetical)
  { value: 'emergency-contact', label: 'Emergency Contact', category: 'trusted' },
  { value: 'nominee', label: 'Nominee / Beneficiary', category: 'trusted' },
  { value: 'poa-holder', label: 'Power of Attorney Holder', category: 'trusted' },
  { value: 'trusted-friend', label: 'Trusted Friend', category: 'trusted' },
  // Other (alphabetical, with "other" last)
  { value: 'friend', label: 'Friend', category: 'other' },
  { value: 'neighbor', label: 'Neighbor', category: 'other' },
  { value: 'other', label: 'Other', category: 'other' },
];

// Initial contacts
export const initialContacts = [
  {
    id: 'contact-1',
    name: 'Jane Doe',
    role: 'spouse',
    category: 'family',
    email: 'jane.doe@email.com',
    phone: '+1 555 123 4567',
    notes: 'Emergency contact, knows location of important documents',
    createdAt: '2024-01-01',
  },
  {
    id: 'contact-2',
    name: 'Smith & Associates',
    role: 'lawyer',
    category: 'legal',
    email: 'contact@smithlaw.com',
    phone: '+1 555 987 6543',
    notes: 'Handles will and estate planning',
    createdAt: '2024-01-15',
  },
];

// Map Type Configurations with Types and Subtypes
export const MAP_TYPE_CONFIG = {
  crypto: {
    types: [
      { value: 'not-specified', label: 'Not specified', hasSubtypes: false },
      {
        value: 'cold-storage',
        label: 'Cold Storage',
        hasSubtypes: true,
        subtypes: [
          { value: 'not-specified', label: 'Not specified' },
          { value: 'air-gapped', label: 'Air-gapped device' },
          { value: 'hardware-wallet', label: 'Hardware wallet (E.g. Ledger, Trezor, Coldcard)' },
          { value: 'paper-wallet', label: 'Paper wallet / metal seed backup' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        value: 'defi',
        label: 'DeFi Protocol',
        hasSubtypes: true,
        subtypes: [
          { value: 'not-specified', label: 'Not specified' },
          { value: 'direct-protocol-staking', label: 'Direct Protocol Staking' },
          { value: 'lending-protocol', label: 'Lending protocol' },
          { value: 'liquidity-pool', label: 'Liquidity pool' },
          { value: 'staking', label: 'Staking platform' },
          { value: 'yield-farming', label: 'Yield farming' },
          { value: 'other', label: 'Other' }
        ]
      },
      { value: 'direct-staking', label: 'Direct Protocol Staking', hasSubtypes: false },
      {
        value: 'exchange',
        label: 'Exchange',
        hasSubtypes: true,
        subtypes: [
          { value: 'not-specified', label: 'Not specified' },
          { value: 'centralized-exchange', label: 'Centralized exchange (E.g. Coinbase, Binance)' },
          { value: 'decentralized-exchange', label: 'Decentralized exchange (DEX)' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        value: 'hot-wallet',
        label: 'Hot Wallet',
        hasSubtypes: true,
        subtypes: [
          { value: 'not-specified', label: 'Not specified' },
          { value: 'browser-extension', label: 'Browser extension (E.g. MetaMask, Phantom)' },
          { value: 'desktop-app', label: 'Desktop application' },
          { value: 'mobile-app', label: 'Mobile app wallet' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        value: 'nft',
        label: 'NFT Collection',
        hasSubtypes: true,
        subtypes: [
          { value: 'not-specified', label: 'Not specified' },
          { value: 'art-collection', label: 'Art collection' },
          { value: 'collectibles', label: 'Collectibles' },
          { value: 'domain-names', label: 'Domain names (ENS, etc.)' },
          { value: 'gaming-assets', label: 'Gaming assets' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        value: 'other',
        label: 'Other',
        hasSubtypes: true,
        subtypes: [
          { value: 'not-specified', label: 'Please specify in notes section' }
        ]
      }
    ]
  },
  shares: {
    types: [
      { value: 'not-specified', label: 'Not specified', hasSubtypes: false },
      { value: 'bank-account', label: 'Bank Account (Checking/Current)', hasSubtypes: false },
      { value: 'bonds', label: 'Bonds', hasSubtypes: false },
      { value: 'brokerage', label: 'Brokerage Account', hasSubtypes: false },
      { value: 'term-deposit', label: 'Certificate of Deposit (CD) / Term Deposit', hasSubtypes: false },
      { value: 'etf', label: 'ETFs', hasSubtypes: false },
      { value: 'mutual-funds', label: 'Mutual Funds', hasSubtypes: false },
      { value: 'pension', label: 'Pension Fund', hasSubtypes: false },
      { value: 'retirement', label: 'Retirement Investment Account', hasSubtypes: false },
      { value: 'retirement-savings', label: 'Retirement Savings Account', hasSubtypes: false },
      { value: 'savings-account', label: 'Savings Account', hasSubtypes: false },
      { value: 'stocks', label: 'Stocks', hasSubtypes: false },
      { value: 'trust', label: 'Trust Account', hasSubtypes: false },
      { value: 'other', label: 'Other', hasSubtypes: false }
    ]
  },
  property: {
    types: [
      { value: 'not-specified', label: 'Not specified', hasSubtypes: false },
      { value: 'agricultural', label: 'Agricultural / Farm Land', hasSubtypes: false },
      {
        value: 'business',
        label: 'Business Interest',
        hasSubtypes: true,
        subtypes: [
          { value: 'not-specified', label: 'Not specified' },
          { value: 'corporation', label: 'Corporation' },
          { value: 'franchise', label: 'Franchise' },
          { value: 'llc', label: 'LLC' },
          { value: 'partnership', label: 'Partnership' },
          { value: 'sole-proprietorship', label: 'Sole Proprietorship' },
          { value: 'other', label: 'Other' }
        ]
      },
      { value: 'commercial', label: 'Commercial Property', hasSubtypes: false },
      { value: 'industrial', label: 'Industrial Property', hasSubtypes: false },
      { value: 'investment', label: 'Investment Property', hasSubtypes: false },
      { value: 'land', label: 'Land / Vacant Property', hasSubtypes: false },
      { value: 'primary-residence', label: 'Primary Residence', hasSubtypes: false },
      { value: 'vacation', label: 'Vacation / Second Home', hasSubtypes: false },
      { value: 'other', label: 'Other', hasSubtypes: false }
    ]
  },
  vehicles: {
    types: [
      { value: 'not-specified', label: 'Not specified', hasSubtypes: false },
      { value: 'aircraft', label: 'Aircraft', hasSubtypes: false },
      { value: 'boat', label: 'Boat / Watercraft', hasSubtypes: false },
      { value: 'car', label: 'Car', hasSubtypes: false },
      { value: 'classic-vintage', label: 'Classic / Vintage Vehicle', hasSubtypes: false },
      {
        value: 'machinery',
        label: 'Machinery / Equipment',
        hasSubtypes: true,
        subtypes: [
          { value: 'not-specified', label: 'Not specified' },
          { value: 'construction', label: 'Construction Equipment' },
          { value: 'farm-equipment', label: 'Farm Equipment' },
          { value: 'garden', label: 'Garden / Lawn Equipment' },
          { value: 'industrial', label: 'Industrial Machinery' },
          { value: 'other', label: 'Other' }
        ]
      },
      { value: 'motorcycle', label: 'Motorcycle', hasSubtypes: false },
      { value: 'recreational', label: 'Recreational Vehicle (RV)', hasSubtypes: false },
      { value: 'trailer', label: 'Trailer', hasSubtypes: false },
      { value: 'truck', label: 'Truck', hasSubtypes: false },
      { value: 'other', label: 'Other', hasSubtypes: false }
    ]
  },
  pets: {
    types: [
      { value: 'not-specified', label: 'Not specified', hasSubtypes: false },
      { value: 'bird', label: 'Bird', hasSubtypes: false },
      { value: 'cat', label: 'Cat', hasSubtypes: false },
      { value: 'dog', label: 'Dog', hasSubtypes: false },
      { value: 'exotic', label: 'Exotic Pet', hasSubtypes: false },
      { value: 'fish', label: 'Fish / Aquarium', hasSubtypes: false },
      { value: 'horse', label: 'Horse / Equine', hasSubtypes: false },
      { value: 'livestock', label: 'Livestock / Farm Animal', hasSubtypes: false },
      { value: 'reptile', label: 'Reptile / Amphibian', hasSubtypes: false },
      { value: 'small-mammal', label: 'Small Mammal (rabbit, guinea pig, etc.)', hasSubtypes: false },
      { value: 'other', label: 'Other', hasSubtypes: false }
    ]
  },
  art: {
    types: [
      { value: 'not-specified', label: 'Not specified', hasSubtypes: false },
      { value: 'antiques', label: 'Antiques', hasSubtypes: false },
      { value: 'coins', label: 'Coins / Numismatics', hasSubtypes: false },
      {
        value: 'fine-art',
        label: 'Fine Art',
        hasSubtypes: true,
        subtypes: [
          { value: 'not-specified', label: 'Not specified' },
          { value: 'mixed-media', label: 'Mixed Media' },
          { value: 'painting', label: 'Painting' },
          { value: 'photography', label: 'Photography' },
          { value: 'print', label: 'Print / Lithograph' },
          { value: 'sculpture', label: 'Sculpture' },
          { value: 'other', label: 'Other' }
        ]
      },
      { value: 'jewelry', label: 'Jewelry', hasSubtypes: false },
      { value: 'memorabilia', label: 'Memorabilia / Sports Cards', hasSubtypes: false },
      { value: 'instruments', label: 'Musical Instruments', hasSubtypes: false },
      { value: 'books', label: 'Rare Books / First Editions', hasSubtypes: false },
      { value: 'stamps', label: 'Stamps / Philately', hasSubtypes: false },
      { value: 'watches', label: 'Watches', hasSubtypes: false },
      { value: 'wine', label: 'Wine / Spirits Collection', hasSubtypes: false },
      { value: 'other', label: 'Other', hasSubtypes: false }
    ]
  },
  social: {
    types: [
      { value: 'not-specified', label: 'Not specified', hasSubtypes: false },
      { value: 'business', label: 'Business Account', hasSubtypes: false },
      { value: 'cloud-storage', label: 'Cloud Storage', hasSubtypes: false },
      { value: 'domain', label: 'Domain Registration', hasSubtypes: false },
      { value: 'email', label: 'Email Account', hasSubtypes: false },
      { value: 'entertainment', label: 'Entertainment / Streaming', hasSubtypes: false },
      { value: 'financial', label: 'Financial Account', hasSubtypes: false },
      { value: 'gaming', label: 'Gaming Account', hasSubtypes: false },
      { value: 'professional', label: 'Professional Network', hasSubtypes: false },
      { value: 'shopping', label: 'Shopping / E-commerce', hasSubtypes: false },
      {
        value: 'social-media',
        label: 'Social Media',
        hasSubtypes: true,
        subtypes: [
          { value: 'not-specified', label: 'Not specified' },
          { value: 'facebook', label: 'Facebook' },
          { value: 'instagram', label: 'Instagram' },
          { value: 'linkedin', label: 'LinkedIn' },
          { value: 'tiktok', label: 'TikTok' },
          { value: 'twitter', label: 'Twitter / X' },
          { value: 'youtube', label: 'YouTube' },
          { value: 'other', label: 'Other' }
        ]
      },
      { value: 'subscription', label: 'Subscription Service', hasSubtypes: false },
      { value: 'hosting', label: 'Web Hosting', hasSubtypes: false },
      { value: 'other', label: 'Other', hasSubtypes: false }
    ]
  },
  physical: {
    types: [
      { value: 'not-specified', label: 'Not specified', hasSubtypes: false },
      { value: 'business-equipment', label: 'Business Equipment', hasSubtypes: false },
      { value: 'electronics', label: 'Electronics', hasSubtypes: false },
      { value: 'filing-cabinet', label: 'Filing Cabinet / Storage', hasSubtypes: false },
      { value: 'furniture', label: 'Furniture', hasSubtypes: false },
      { value: 'home-safe', label: 'Home Safe', hasSubtypes: false },
      {
        value: 'documents',
        label: 'Important Documents',
        hasSubtypes: true,
        subtypes: [
          { value: 'not-specified', label: 'Not specified' },
          { value: 'financial', label: 'Financial Records' },
          { value: 'identity', label: 'Identity Documents (passport, birth certificate)' },
          { value: 'insurance', label: 'Insurance Policies' },
          { value: 'legal', label: 'Legal Documents' },
          { value: 'medical', label: 'Medical Records' },
          { value: 'property', label: 'Property Deeds / Titles' },
          { value: 'other', label: 'Other' }
        ]
      },
      { value: 'keys-access', label: 'Keys / Access Cards', hasSubtypes: false },
      { value: 'safe-deposit', label: 'Safe Deposit Box', hasSubtypes: false },
      { value: 'sports-equipment', label: 'Sports Equipment', hasSubtypes: false },
      { value: 'tools', label: 'Tools & Equipment', hasSubtypes: false },
      { value: 'other', label: 'Other', hasSubtypes: false }
    ]
  },
  insurance: {
    types: [
      { value: 'not-specified', label: 'Not specified', hasSubtypes: false },
      { value: 'auto', label: 'Auto / Vehicle Insurance', hasSubtypes: false },
      { value: 'business', label: 'Business Insurance', hasSubtypes: false },
      { value: 'disability', label: 'Disability Insurance', hasSubtypes: false },
      { value: 'health', label: 'Health Insurance', hasSubtypes: false },
      { value: 'home', label: 'Home / Property Insurance', hasSubtypes: false },
      {
        value: 'life',
        label: 'Life Insurance',
        hasSubtypes: true,
        subtypes: [
          { value: 'not-specified', label: 'Not specified' },
          { value: 'term', label: 'Term Life' },
          { value: 'whole', label: 'Whole Life' },
          { value: 'universal', label: 'Universal Life' },
          { value: 'variable', label: 'Variable Life' },
          { value: 'other', label: 'Other' }
        ]
      },
      { value: 'long-term-care', label: 'Long-Term Care Insurance', hasSubtypes: false },
      { value: 'pet', label: 'Pet Insurance', hasSubtypes: false },
      { value: 'renters', label: 'Renters Insurance', hasSubtypes: false },
      { value: 'travel', label: 'Travel Insurance', hasSubtypes: false },
      { value: 'umbrella', label: 'Umbrella / Liability Insurance', hasSubtypes: false },
      { value: 'other', label: 'Other', hasSubtypes: false }
    ]
  },
  'final-wishes': {
    types: [
      { value: 'not-specified', label: 'Not specified', hasSubtypes: false },
      {
        value: 'burial-method',
        label: 'Burial / Cremation Preference',
        hasSubtypes: true,
        subtypes: [
          { value: 'not-specified', label: 'Not specified' },
          { value: 'burial', label: 'Traditional Burial' },
          { value: 'cremation', label: 'Cremation' },
          { value: 'green-burial', label: 'Green / Natural Burial' },
          { value: 'aquamation', label: 'Aquamation / Water Cremation' },
          { value: 'body-donation', label: 'Body Donation to Science' },
          { value: 'other', label: 'Other' }
        ]
      },
      { value: 'ceremony-type', label: 'Ceremony / Service Type', hasSubtypes: false },
      { value: 'location-preference', label: 'Location Preference', hasSubtypes: false },
      { value: 'music-readings', label: 'Music & Readings', hasSubtypes: false },
      { value: 'flowers-donations', label: 'Flowers / Donations Preference', hasSubtypes: false },
      { value: 'wake-reception', label: 'Wake / Reception Wishes', hasSubtypes: false },
      { value: 'dress-code', label: 'Dress Code / Attire', hasSubtypes: false },
      { value: 'attendees', label: 'Guest / Attendee Wishes', hasSubtypes: false },
      { value: 'obituary', label: 'Obituary Preferences', hasSubtypes: false },
      { value: 'memorial', label: 'Memorial / Headstone Wishes', hasSubtypes: false },
      { value: 'ashes-scattering', label: 'Ashes Scattering Location', hasSubtypes: false },
      { value: 'pre-paid', label: 'Pre-paid Funeral Plan', hasSubtypes: false },
      { value: 'special-request', label: 'Special Request', hasSubtypes: false },
      { value: 'other', label: 'Other', hasSubtypes: false }
    ]
  },
  'other-info': {
    types: [
      { value: 'not-specified', label: 'Not specified', hasSubtypes: false },
      {
        value: 'document',
        label: 'Document / Record',
        hasSubtypes: true,
        subtypes: [
          { value: 'not-specified', label: 'Not specified' },
          { value: 'certificate', label: 'Certificate (birth, marriage, etc.)' },
          { value: 'contract', label: 'Contract / Agreement' },
          { value: 'license', label: 'License / Permit' },
          { value: 'membership', label: 'Membership / Subscription' },
          { value: 'warranty', label: 'Warranty / Guarantee' },
          { value: 'other', label: 'Other Document' }
        ]
      },
      { value: 'account', label: 'Account / Login', hasSubtypes: false },
      { value: 'arrangement', label: 'Arrangement / Agreement', hasSubtypes: false },
      { value: 'contact-info', label: 'Important Contact Info', hasSubtypes: false },
      { value: 'instruction', label: 'Instruction / How-to', hasSubtypes: false },
      { value: 'location', label: 'Location / Address', hasSubtypes: false },
      { value: 'password-hint', label: 'Password Hint / Recovery Info', hasSubtypes: false },
      { value: 'reminder', label: 'Reminder / Note to Self', hasSubtypes: false },
      { value: 'other', label: 'Other', hasSubtypes: false }
    ]
  }
};

// Map Types with their configurations
export const MAP_TYPES = [
  // Crypto first
  {
    id: 'crypto',
    name: 'Crypto & Digital Assets',
    description: 'Digital currencies, tokens, and blockchain-based assets',
    helperText: 'Focus on what exists, what matters, and where access is managed—no secrets.',
    image: 'https://images.unsplash.com/photo-1762433813475-e6b761cc23d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    status: 'configured',
  },
  // Then alphabetically
  {
    id: 'art',
    name: 'Art, Collectibles & Valuables',
    description: 'Artwork, collections, jewelry, and precious items',
    helperText: 'Focus on provenance/handling and specialist sale channels.',
    image: 'https://images.unsplash.com/photo-1612690119274-8819a81c13a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    status: 'configured',
  },
  {
    id: 'final-wishes',
    name: 'Final Wishes & Funeral',
    description: 'Funeral preferences, burial wishes, and ceremony arrangements',
    helperText: 'Capture your preferences to guide loved ones—burial vs cremation, music, location, special requests.',
    image: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    status: 'not-started',
  },
  {
    id: 'insurance',
    name: 'Insurance Policies',
    description: 'Life, health, property, and other insurance coverage',
    helperText: 'Focus on what policies exist and who to contact—no policy numbers or account details.',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    status: 'configured',
  },
  {
    id: 'other-info',
    name: 'Other Documents & Info',
    description: 'Miscellaneous documents, arrangements, and information',
    helperText: 'A catch-all for anything that doesn\'t fit elsewhere—documents, accounts, reminders, instructions.',
    image: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    status: 'not-started',
  },
  {
    id: 'pets',
    name: 'Pets & Animals',
    description: 'Companion animals and their care arrangements',
    helperText: 'Focus on care continuity and informal arrangements.',
    image: 'https://images.unsplash.com/photo-1713948844922-42053dd40980?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    status: 'configured',
  },
  {
    id: 'physical',
    name: 'Physical Records & Offline Storage',
    description: 'Paper documents, safe deposits, and physical storage',
    helperText: 'Focus on what exists and general location context—no combinations or exact locations.',
    image: 'https://images.unsplash.com/photo-1623305466248-b0447de035e8?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    status: 'configured',
  },
  {
    id: 'property',
    name: 'Property, Land & Businesses',
    description: 'Real estate, land holdings, and business interests',
    helperText: 'Focus on ownership structures and specialist handling notes.',
    image: 'https://images.unsplash.com/photo-1609391537609-607491c91819?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    status: 'configured',
  },
  {
    id: 'shares',
    name: 'Shares & Investments',
    description: 'Stocks, bonds, funds, and investment accounts',
    helperText: 'Focus on where investments are held and recovery pathways—no account numbers required.',
    image: 'https://images.unsplash.com/photo-1655012325190-89c9153a563c?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    status: 'configured',
  },
  {
    id: 'social',
    name: 'Social Media & Online Accounts',
    description: 'Digital presence and online service accounts',
    helperText: 'Focus on intent (leave, memorialise, close) and privacy boundaries.',
    image: 'https://images.unsplash.com/photo-1654445846261-87210420d33e?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    status: 'configured',
  },
  {
    id: 'vehicles',
    name: 'Vehicles, Vessels & Machinery',
    description: 'Cars, boats, aircraft, and equipment',
    helperText: 'Highlight specialist resale/ownership complexity and intent.',
    image: 'https://images.unsplash.com/photo-1730542254054-b13c967dad56?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    status: 'configured',
  },
  // Formal Arrangements link tile (last)
  {
    id: 'formal-arrangements',
    name: 'Formal Arrangements',
    description: 'Will, power of attorney, executors, and legal documentation',
    helperText: 'Review and manage your formal legal arrangements.',
    image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    status: 'configured',
    isLink: true,
    linkTo: '/will-status',
  },
];

// Initial mock entries for maps
export const initialEntries = {
  crypto: [
    {
      id: 'crypto-1',
      label: 'Primary Bitcoin Holdings',
      type: 'cold-storage',
      subtype: 'hardware-wallet',
      location: 'Hardware Wallet - Ledger',
      priority: 'critical',
      notes: 'Main long-term holdings. Recovery phrase stored separately.',
      firstAction: 'Contact family lawyer for access instructions.',
      selfCustody: true,
      lastUpdated: '2024-01-15',
    },
  ],
  shares: [
    {
      id: 'shares-1',
      label: 'Retirement Account - 401(k)',
      type: 'retirement',
      subtype: 'not-specified',
      location: 'Vanguard',
      priority: 'high',
      notes: 'Index funds portfolio. Beneficiary already designated.',
      firstAction: 'Contact Vanguard directly with death certificate.',
      tickerSymbol: 'VTSAX, VTIAX',
      quantity: 'Various holdings',
      estimatedValue: '$150,000',
      broker: 'Vanguard',
      accountReference: 'Account ending in 4521',
      purchaseDate: '',
      costBasis: 'Various',
      documentation: 'Annual statements in email archive',
      lastUpdated: '2024-02-20',
    },
    {
      id: 'shares-2',
      label: 'Individual Brokerage',
      type: 'brokerage',
      subtype: 'not-specified',
      location: 'Interactive Brokers',
      priority: 'medium',
      notes: 'Mix of stocks and ETFs.',
      firstAction: 'Log in details with password manager.',
      tickerSymbol: 'AAPL, MSFT, VOO',
      quantity: '150 shares total',
      estimatedValue: '$45,000',
      broker: 'Interactive Brokers',
      accountReference: 'Account ending in 7890',
      purchaseDate: '2021-03-15',
      costBasis: '$32,000',
      documentation: 'Monthly statements available online',
      lastUpdated: '2024-01-10',
    },
  ],
  property: [
    {
      id: 'property-1',
      label: 'Family Home',
      type: 'primary-residence',
      subtype: 'not-specified',
      location: '',
      priority: 'critical',
      propertyAddress: '123 Main Street, Anytown, ST 12345',
      ownershipStatus: 'mortgaged',
      deedLocation: 'Safe deposit box at First National Bank',
      mortgageProvider: 'Wells Fargo Home Mortgage',
      solicitorContact: 'Smith & Associates - Contact: John Smith (555-0123)',
      notes: 'Joint ownership with spouse. Mortgage refinanced in 2022.',
      firstAction: 'Contact solicitor for deed documents, mortgage statements in home office filing cabinet.',
      lastUpdated: '2024-03-01',
    },
  ],
  vehicles: [
    {
      id: 'vehicles-1',
      label: '1967 Ford Mustang',
      type: 'classic-vintage',
      subtype: 'not-specified',
      location: '',
      priority: 'high',
      vehicleMake: 'Ford',
      vehicleModel: 'Mustang Fastback',
      vehicleYear: '1967',
      vehicleRegistration: 'CLSC 67',
      vehicleVin: '7R02C123456',
      vehicleOwnershipStatus: 'owned-outright',
      sharedOwnership: true,
      storageLocation: 'Classic car storage facility - Bay 12, Prestige Auto Storage',
      insuranceDetails: 'Hagerty Classic Car Insurance, Agreed value policy',
      serviceContact: 'Vintage Motors Restoration - Mike (555-7890)',
      notes: 'Part of classic car club syndicate with 3 other members. Verbal agreement on usage rotation.',
      firstAction: 'Contact club secretary Tom regarding syndicate arrangements.',
      lastUpdated: '2024-02-15',
    },
  ],
  pets: [
    {
      id: 'pets-1',
      label: 'Max',
      type: 'dog',
      subtype: 'not-specified',
      location: '',
      priority: 'critical',
      petBreed: 'Golden Retriever',
      petDateOfBirth: 'March 2019 (~5 years old)',
      vetContact: 'Happy Paws Veterinary Clinic - Dr. Sarah Johnson (555-4567)',
      medicalNeeds: 'Daily joint supplement, annual heartworm medication. Anxious during thunderstorms.',
      petOwnershipArrangements: 'Registered in my name, adoption papers from Golden Rescue',
      verbalArrangements: true,
      emergencyCaregiver: 'Sister Jane (555-1234) - has spare house key, knows routine',
      notes: 'Walks twice daily, breakfast at 7am, dinner at 6pm. Loves tennis balls. Afraid of vacuum cleaner.',
      firstAction: 'Contact sister Jane immediately - she has agreed to take care of Max.',
      lastUpdated: '2024-03-10',
    },
  ],
  art: [
    {
      id: 'art-1',
      label: 'Sunset over Mountains',
      type: 'fine-art',
      subtype: 'painting',
      location: '',
      priority: 'high',
      artistCreator: 'Local Artist - Sarah Mitchell',
      yearPeriod: '2019',
      mediumMaterials: 'Oil on canvas, 24x36 inches',
      provenance: 'Purchased directly from artist at gallery exhibition 2019',
      appraisedValue: '$3,500',
      appraisalDate: 'Purchase price, never formally appraised',
      artStorageLocation: 'Displayed in living room, above fireplace',
      artInsurance: 'Covered under home contents policy',
      authentication: 'Certificate of authenticity from artist, receipt in filing cabinet',
      intendedDisposition: 'keep-in-family',
      notes: 'Meaningful piece purchased on wedding anniversary. Sarah to have first option if sold.',
      firstAction: 'Contact gallery if appraisal needed for insurance purposes.',
      lastUpdated: '2024-02-01',
    },
    {
      id: 'art-2',
      label: 'Grandmother\'s Engagement Ring',
      type: 'jewelry',
      subtype: 'not-specified',
      location: '',
      priority: 'critical',
      artistCreator: 'Tiffany & Co.',
      yearPeriod: 'Circa 1952',
      mediumMaterials: 'Platinum setting, 1.2ct diamond solitaire',
      provenance: 'Inherited from grandmother 2010, originally purchased by grandfather',
      appraisedValue: '$8,500',
      appraisalDate: 'March 2023',
      artStorageLocation: 'Home safe when not worn',
      artInsurance: 'Scheduled item on home policy with Chubb, agreed value',
      authentication: 'GIA certificate, appraisal document in safe deposit box',
      intendedDisposition: 'gift-to-specific',
      notes: 'To go to eldest daughter. Appraisal and GIA cert should transfer with ring.',
      firstAction: 'Ensure daughter knows location of safe and combination.',
      lastUpdated: '2024-03-15',
    },
  ],
  social: [
    {
      id: 'social-1',
      label: '@johndoe',
      type: 'social-media',
      subtype: 'linkedin',
      location: '',
      priority: 'medium',
      platformName: 'LinkedIn',
      accountUrl: 'linkedin.com/in/johndoe',
      accountDisposition: 'memorialize',
      notes: 'Professional network with 500+ connections. Would like profile memorialized if possible.',
      firstAction: 'LinkedIn has a memorialization process - family should contact support.',
      lastUpdated: '2024-01-05',
    },
    {
      id: 'social-2',
      label: 'john.doe@gmail.com',
      type: 'email',
      subtype: 'not-specified',
      location: '',
      priority: 'high',
      platformName: 'Gmail / Google Account',
      accountUrl: 'mail.google.com',
      accountDisposition: 'transfer',
      notes: 'Primary email. Contains important correspondence. Google Inactive Account Manager configured.',
      firstAction: 'Google Inactive Account Manager set to notify spouse after 3 months inactivity.',
      lastUpdated: '2024-02-20',
    },
    {
      id: 'social-3',
      label: 'Personal Facebook',
      type: 'social-media',
      subtype: 'facebook',
      location: '',
      priority: 'low',
      platformName: 'Facebook',
      accountUrl: 'facebook.com/johndoe',
      accountDisposition: 'close',
      notes: 'Rarely used, prefer it to be deleted.',
      firstAction: 'No urgent action - can be closed when convenient.',
      lastUpdated: '2024-01-10',
    },
  ],
  physical: [
    {
      id: 'physical-1',
      label: 'Property Deed - Family Home',
      type: 'documents',
      subtype: 'property',
      location: '',
      priority: 'critical',
      physicalLocation: 'Safe deposit box at First National Bank, Box #247',
      documentFormat: 'original',
      accessInfo: 'Spouse is co-signer on safe deposit box, key in home safe',
      relatedTo: 'Family Home (Property map)',
      retentionNotes: 'Keep forever - original deed for property ownership',
      notes: 'Original deed with both names. Copy also with solicitor Smith & Associates.',
      firstAction: 'Contact bank branch manager for safe deposit access.',
      lastUpdated: '2024-01-20',
    },
    {
      id: 'physical-2',
      label: 'Birth Certificates & Passports',
      type: 'documents',
      subtype: 'identity',
      location: '',
      priority: 'high',
      physicalLocation: 'Home safe in master bedroom closet',
      documentFormat: 'original',
      accessInfo: 'Spouse knows combination, written in password manager',
      relatedTo: 'All family members',
      retentionNotes: 'Keep forever - renew passports as needed',
      notes: 'Contains birth certificates for entire family and current passports.',
      firstAction: 'Home safe combination in password manager under "Home Safe".',
      lastUpdated: '2024-02-15',
    },
    {
      id: 'physical-3',
      label: 'Tax Returns 2019-2024',
      type: 'documents',
      subtype: 'financial',
      location: '',
      priority: 'medium',
      physicalLocation: 'Filing cabinet in home office, drawer 2',
      documentFormat: 'both',
      accessInfo: 'Digital copies also in Google Drive / Tax folder',
      relatedTo: 'Annual tax filings',
      retentionNotes: 'Keep for 7 years minimum, then can shred',
      notes: 'Organized by year. Digital backups exist.',
      firstAction: 'May be needed for estate tax purposes.',
      lastUpdated: '2024-04-15',
    },
  ],
  insurance: [
    {
      id: 'insurance-1',
      label: 'Term Life Insurance',
      type: 'life',
      subtype: 'term',
      location: '',
      priority: 'critical',
      insuranceProvider: 'Prudential',
      policyReference: 'Policy ending in 7821',
      coverageAmount: '$500,000 death benefit',
      premiumDetails: '$85/month, auto-debit from checking',
      renewalDate: 'Term policy expires 2040',
      beneficiaries: 'Spouse (100% primary), Children (contingent)',
      agentContact: 'Sarah Williams, Prudential Agency (555-9876)',
      policyDocLocation: 'Filing cabinet, Insurance folder; also in email',
      coverageDescription: '20-year term life, income replacement for family. No cash value.',
      notes: 'Purchased in 2020 after mortgage. Review beneficiaries if circumstances change.',
      firstAction: 'Contact Prudential claims department with death certificate.',
      lastUpdated: '2024-03-01',
    },
    {
      id: 'insurance-2',
      label: 'Homeowners Insurance',
      type: 'home',
      subtype: 'not-specified',
      location: '',
      priority: 'high',
      insuranceProvider: 'State Farm',
      policyReference: 'Policy ending in 3456',
      coverageAmount: '$450,000 dwelling, $100,000 contents, $300,000 liability',
      premiumDetails: '$1,800/year, paid annually in March',
      renewalDate: 'March 15 annually, auto-renews',
      beneficiaries: 'Mortgage company listed as loss payee',
      agentContact: 'Mike Johnson, State Farm (555-4321)',
      policyDocLocation: 'Email archive, policy documents folder',
      coverageDescription: 'Covers primary residence, personal property, and liability. Includes flood rider.',
      notes: 'Bundled with auto for discount. Jewelry rider added for engagement ring.',
      firstAction: 'Keep current for property protection; may need to update after estate settlement.',
      lastUpdated: '2024-03-15',
    },
    {
      id: 'insurance-3',
      label: 'Auto Insurance - Both Vehicles',
      type: 'auto',
      subtype: 'not-specified',
      location: '',
      priority: 'medium',
      insuranceProvider: 'State Farm',
      policyReference: 'Policy ending in 3457',
      coverageAmount: '$100k/$300k liability, $50k property, Full comprehensive',
      premiumDetails: '$2,400/year for both vehicles',
      renewalDate: 'March 15 annually with home policy',
      beneficiaries: 'N/A',
      agentContact: 'Mike Johnson, State Farm (555-4321)',
      policyDocLocation: 'Insurance cards in glove boxes, full policy in email',
      coverageDescription: 'Covers 2022 Toyota and 1967 Mustang. Classic car has agreed value.',
      notes: 'Mustang on classic car coverage with Hagerty rider through State Farm.',
      firstAction: 'May need to adjust coverage based on vehicle disposition.',
      lastUpdated: '2024-03-15',
    },
  ],
  'final-wishes': [],
  'other-info': [],
};

// Initial map notes/intent
export const initialMapNotes = {
  crypto: 'I have accumulated digital assets over the years as part of diversified savings. The most important thing is that recovery access is handled carefully and not rushed.',
  shares: 'These investments represent long-term planning. Most accounts have designated beneficiaries already.',
  property: 'The family home is our primary asset. All documents are with our lawyer.',
  vehicles: 'The classic Mustang has sentimental value and shared ownership arrangement with car club.',
  pets: 'Max is family. Sister Jane has agreed to take him if needed.',
  art: 'A few meaningful pieces with sentimental value. Jewelry should stay in the family.',
  social: 'I would prefer most accounts to be closed rather than left abandoned. LinkedIn can be memorialised.',
  physical: 'Important documents are in the home safe and bank safe deposit box. Spouse has access to both.',
  insurance: 'Life insurance is the most important - ensures family is protected. Contact agent Sarah for claims.',
  'final-wishes': '',
  'other-info': '',
};

// Continuity configuration
export const initialContinuityConfig = {
  isConfigured: false,
  checkInFrequency: 12,
  reminderCount: 3,
  contactMethods: {
    email: true,
    sms: false,
    alternativeEmail: false,
  },
  triggerAction: 'notify',
  nominees: [],
  accessScope: {
    crypto: true,
    shares: true,
    property: true,
    vehicles: false,
    pets: true,
    art: false,
    social: false,
    physical: true,
    insurance: true,
    'final-wishes': false,
    'other-info': false,
  },
  lastCheckIn: null,
  nextCheckIn: null,
};

// Will status configuration
export const initialWillStatus = {
  // Basic will info
  willExists: 'unsure',
  dateCreated: '',
  dateUpdated: '',
  heldWith: '',
  firmName: '',
  location: '',
  primaryContact: {
    name: '',
    role: '',
    email: '',
    phone: '',
  },
  
  // Review & Currency
  reminderDate: '',
  lastReviewedDate: '',
  confidenceLevel: '',
  
  // Scope
  scope: {
    personalAssets: false,
    property: false,
    businessInterests: false,
    digitalAssets: false,
    other: false,
  },
  
  // Executors
  executor1: {
    name: '',
    relationship: '',
    email: '',
    phone: '',
  },
  executor2: {
    name: '',
    relationship: '',
    email: '',
    phone: '',
  },
  executorNotes: '',
  
  // Power of Attorney
  poaExists: 'unsure',
  poaType: '',
  poaOtherType: '',
  poaHolders: [],
  poaActivation: '',
  
  // Guardianship
  guardianshipExists: 'not-applicable',
  guardianshipNotes: '',
  
  // Trusts
  trustsExist: 'unsure',
  
  // Professional Advisors
  legalAdvisor: {
    name: '',
    role: '',
    email: '',
    phone: '',
  },
  otherAdvisors: [],
  
  // Relationship to Lifely
  lifelyReferenced: 'unsure',
  
  // Other documents and notes
  otherDocuments: [],
  notes: '',
};

// User profile (placeholder)
export const mockUser = {
  id: 'user-1',
  username: 'James',
  email: 'james@example.com',
  avatar: null,
  createdAt: '2024-01-01',
  lastLogin: '2024-03-15',
};

// Status labels
export const STATUS_LABELS = {
  'not-configured': 'Not Configured',
  'configured': 'Configured',
};

// Priority levels
export const PRIORITY_LEVELS = [
  {
    value: 'critical',
    label: 'Critical - Needs immediate attention',
    description: 'Items that require urgent action or represent significant value/complexity',
  },
  {
    value: 'high',
    label: 'High - Important, time-sensitive',
    description: 'Important items that should be addressed soon',
  },
  {
    value: 'medium',
    label: 'Medium - Important, not urgent',
    description: 'Important items that can be reviewed at a later time',
  },
  {
    value: 'low',
    label: 'Low - For reference',
    description: 'Items maintained for informational purposes',
  },
];

// Priority labels (for display)
export const PRIORITY_LABELS = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

// Role options for nominees
export const NOMINEE_ROLES = [
  { value: 'partner', label: 'Partner / Spouse' },
  { value: 'parent', label: 'Parent' },
  { value: 'child', label: 'Child' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'lawyer', label: 'Lawyer' },
  { value: 'law-firm', label: 'Law Firm' },
  { value: 'trustee', label: 'Trustee' },
  { value: 'executor', label: 'Executor' },
  { value: 'other', label: 'Other' },
];

// Will holder options
export const WILL_HOLDER_OPTIONS = [
  { value: 'lawyer', label: 'Lawyer / Law Firm' },
  { value: 'trustee', label: 'Trustee' },
  { value: 'personal', label: 'Stored Personally' },
  { value: 'other', label: 'Other' },
];

// Check-in frequency options
export const CHECKIN_FREQUENCY_OPTIONS = [
  { value: 6, label: '6 months' },
  { value: 12, label: '12 months (recommended)' },
  { value: 24, label: '24 months' },
  { value: 'custom', label: 'Custom' },
];

// Reminder count options
export const REMINDER_COUNT_OPTIONS = [
  { value: 1, label: '1 reminder' },
  { value: 2, label: '2 reminders' },
  { value: 3, label: '3 reminders (recommended)' },
  { value: 'custom', label: 'Custom' },
];

// Helper function to sort options alphabetically with "Not specified" first and "Other" last
function sortOptions(options) {
  return [...options].sort((a, b) => {
    // "Not specified" always first
    if (a.value === 'not-specified') return -1;
    if (b.value === 'not-specified') return 1;
    // "Other" always last
    if (a.value === 'other') return 1;
    if (b.value === 'other') return -1;
    // Alphabetical by label
    return a.label.localeCompare(b.label);
  });
}

// Helper function to get types for a map (sorted alphabetically)
export function getTypesForMap(mapId) {
  const config = MAP_TYPE_CONFIG[mapId];
  if (!config) return [{ value: 'not-specified', label: 'Not specified', hasSubtypes: false }];
  return sortOptions(config.types);
}

// Helper function to get subtypes for a type (sorted alphabetically)
export function getSubtypesForType(mapId, typeValue) {
  const config = MAP_TYPE_CONFIG[mapId];
  if (!config) return [];
  
  const typeConfig = config.types.find(t => t.value === typeValue);
  if (!typeConfig || !typeConfig.hasSubtypes) return [];
  
  return sortOptions(typeConfig.subtypes || []);
}

// Helper function to check if a type has subtypes
export function typeHasSubtypes(mapId, typeValue) {
  const config = MAP_TYPE_CONFIG[mapId];
  if (!config) return false;
  
  const typeConfig = config.types.find(t => t.value === typeValue);
  return typeConfig ? typeConfig.hasSubtypes : false;
}

// Helper function to get type label
export function getTypeLabel(mapId, typeValue) {
  const config = MAP_TYPE_CONFIG[mapId];
  if (!config) return typeValue;
  
  const typeConfig = config.types.find(t => t.value === typeValue);
  return typeConfig ? typeConfig.label : typeValue;
}

// Helper function to get subtype label
export function getSubtypeLabel(mapId, typeValue, subtypeValue) {
  const subtypes = getSubtypesForType(mapId, typeValue);
  const subtype = subtypes.find(s => s.value === subtypeValue);
  return subtype ? subtype.label : subtypeValue;
}
